import { Moment } from 'moment-timezone';
import { isSubscribed } from '../functions';
import HttpMethod from '../../../app/enums/HttpMethod';
import Practitioner from '../../../app/models/Practitioner';
import CalendarEvent from '../../../app/models/CalendarEvent';
import { CalendarEventsByDay, EventPayload } from '../../../app/support/types';
import { isToday, localizedRoute, moment, __, __t } from '../../../app/support/helpers';
import { onClick, range, slice } from '../../../app/support/utils';

export default function() {

	if (! isSubscribed()) {

        const alternatives = Practitioner.new(window.doctori.alternatives as []);

        const urls = alternatives.map((alternative) => {
            const params = {
                id: alternative.id,
                start: moment().format('YYYY-MM-DDTHH:mm:ss'),
                end: moment().add(3, 'day').format('YYYY-MM-DD'),
            }
            return localizedRoute('frontend.practitioners.availabilities', params)
        });

        const awaitJson = (responses) => Promise.all(responses.map((response) => {
            if (response.ok) return response.json();
            throw new Error(response.statusText);
        }));

        Promise.all(urls.map((url) => fetch(url)))
            .then(awaitJson)
            .then((data) => {
                data.forEach((availabilities, index) => {
                    const practitioner   = alternatives[index];
                    const calendarEvents = CalendarEvent.new(availabilities as []);
                    const calendar       = renderAlternativeAvailabilities(calendarEvents, generateThreeDaysTimePeriod());
                    document.querySelector(`.calendarWrapper[data-id="${practitioner.id}"]`).innerHTML = calendar;
                });
            })
            .catch((error) => console.log("Error ", error));
	}

    onClick('.nextAlternativeAvailability', (e: Event, payload: EventPayload) => {
        e.preventDefault();
        const target           = payload.target;
        const nextAvailability = CalendarEvent.new(JSON.parse(target.getAttribute('data-next-availability')) as {});
        const calendarWrapper  = document.querySelector(`.calendarWrapper[data-id="${nextAvailability.practitioner_id}"]`) as HTMLElement;
        const loader           = calendarWrapper.querySelector('.bar-loader') as HTMLElement;

        const params = {
            id   : nextAvailability.practitioner_id,
            start: moment(nextAvailability.start).format('YYYY-MM-DDTHH:mm:ss'),
            end  : moment(nextAvailability.start).add(3, 'day').format('YYYY-MM-DD'),
        }

        loader.style.display = 'block';

        fetch(localizedRoute('frontend.practitioners.availabilities', params))
            .then((response) => response.json())
            .then((availabilities) => {
                const calendarEvents = CalendarEvent.new(availabilities as []);
                const calendar       = renderAlternativeAvailabilities(calendarEvents, generateThreeDaysTimePeriod(nextAvailability));
                calendarWrapper.innerHTML = calendar;
            })
            .catch((error) => console.log("Error ", error))
            .finally(() => {
                loader.style.display = 'none';
            });
    });
}

/**
 * Render alternative availabilities
 *
 * @param {Practitioner} alternative
 * @param {CalendarEvent[]} availabilities
 * @returns {string}
 */
 export function renderAlternativeAvailabilities(availabilities: CalendarEvent[], period: Moment[]): string {
    const bookableEvents      = availabilities.filter((calendarEvent: CalendarEvent) => calendarEvent.isBookable());
    const _availabilities     = bookableEvents.length ? bookableEvents : availabilities;
    const hasOnlyClosestEvent = _availabilities.every((calendarEvent) => calendarEvent.isClosest());
    const closestAvailability = _availabilities.find((calendarEvent) => calendarEvent.isClosest());
    const availabilitiesByDay = CalendarEvent.groupByDay(_availabilities);

    return `
        <div class="table_time_detail">
            <div>
                <div class="right_header">
                    <div class="bar-loader" style="display: none;"></div>
                    <ul class="ul_date">
                        ${ renderCalendarDays(period) }
                    </ul>
                </div>
            </div>

            <div class="liste_times font-tm">
                ${ hasOnlyClosestEvent
                    ?  `<div class="availability-message-container">
                            <p class="availability-message">
                                <a href="#" class="nextAlternativeAvailability" data-next-availability='${JSON.stringify(closestAvailability)}' rel="nofollow">
                                    ${__('next_appointment_on')} ${closestAvailability.startDate('DD/MM/YYYY')}
                                </a>
                                <span class="icon_right"></span>
                            </p>
                        </div>`
                    : renderCalendarColumns(period, availabilitiesByDay)
                }
            </div>
        </div>
    `;
}

/**
 * Render calendar days
 *
 * @param {Moment[]} period
 * @returns {string}
 */
 function renderCalendarDays(period: Moment[]): string {
    return period.map((date: Moment) => {
        return renderCalendarDay(date);
    }).join('');
}

/**
 * Render calendar day
 *
 * @param {Moment} date
 * @returns {string}
 */
function renderCalendarDay(date: Moment): string {
    return `
        <li>
            <span class="date_day">${date.format('ddd')}</span>
            <span class="date_value">${date.format('DD/MM')}</span>
        </li>
    `;
}

/**
 * Render calendar columns
 *
 * @param {Moment[]} period
 * @param {CalendarEventsByDay} availabilitiesByDay
 * @returns {string}
 */
 function renderCalendarColumns(period: Moment[], availabilitiesByDay: CalendarEventsByDay): string {
    return period.map((date: Moment) => {
        const day            = date.format('YYYY-MM-DD');
        let availabilities   = availabilitiesByDay[day] || [];
        if (availabilities.length && isToday(date)) {
            // get only bookable availabilities
            availabilities = availabilities.filter((availability) => {
                return availability.isBookable();
            });
        }
        // get only the first three availabilities
        const _availabilities = availabilities.slice(0, 3);

        return renderCalendarColumn(_availabilities);
    }).join('');
}

/**
 * Render calendar column
 *
 * @param {CalendarEvent[]} availabilities
 * @returns {string}
 */
 function renderCalendarColumn(availabilities: CalendarEvent[]): string {

    return `
        <div class="times">
            <ul class="ul_date_value2">
                <div class="date_top">
                    ${ renderCalendarEvents(availabilities) }
                </div>
            </ul>
        </div>
    `;
}

/**
 * Render Calendar Events
 *
 * @param {CalendarEvent[]} calendarEvents
 * @returns {string}
 */
 function renderCalendarEvents(calendarEvents: CalendarEvent[]): string {
    const events = calendarEvents.map((calendarEvent: CalendarEvent) => {
        return renderCalendarEvent(calendarEvent);
    }).join('');

    const totalFakeEvents = 3 - calendarEvents.length;
    return events + renderFakeEvents(totalFakeEvents);
}

/**
 * Render calendar event
 *
 * @param {CalendarEvent} calendarEvent
 * @returns {string}
 */
function renderCalendarEvent(calendarEvent: CalendarEvent): string {
    return `
        <li>
            <a
                href="${ calendarEvent.isBookable() ? calendarEvent.bookingUrl() : 'javascript:void(0)' }"
                class="time ${ calendarEvent.isBookable() ? 'bookAppointment' : 'disabled-rdv' } ${calendarEvent.isRemote() ? 'video-call-calendar' : ''}"
                rel="nofollow"
            >
                ${ calendarEvent.startTime() }
            </a>
        </li>
    `;
}

/**
 * Render fake events
 *
 * @param {number} total
 * @returns {string}
 */
function renderFakeEvents(total: number): string {
    return range(1, total).map(() => renderFakeEvent()).join('');
}

/**
 * Render fake event
 *
 * @returns {string}
 */
function renderFakeEvent(): string {
    return `
        <li>
            <a onclick="return:false;" class="time disabled-rdv" rel="nofollow">
                --
            </a>
        </li>
    `;
}

function generateThreeDaysTimePeriod(calendarEvent?: CalendarEvent): Moment[] {
    const {start, end} = threeDaysInterval(calendarEvent);
    // @ts-ignore
    const range = moment().range(start, end);
    return Array.from(range.by('days', { excludeEnd: true })) as Moment[];
}

function threeDaysInterval(calendarEvent?: CalendarEvent): {start: string, end: string} {
    const start = moment(calendarEvent?.start).format('YYYY-MM-DD');
    const end   = moment(calendarEvent?.start).add(3, 'day').format('YYYY-MM-DD');
    return {start, end};
}



